import { useEffect, useState, useMemo, useCallback } from 'react';
import { BarChart3, Download, ArrowUpDown, ArrowUp, ArrowDown, ArrowDownUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import PracticeActivityGroups from '@/components/activities/PracticeActivityGroups';
import ActivityRowReadOnly from '@/components/activities/ActivityRowReadOnly';
import type { PracticeGroup } from '@/components/activities/practiceActivityGroupTypes';
import { useActivityTypeStore } from '@/store/activityTypeStore';
import type { Activity } from '@/services/activityService';
import { apiClient } from '@/utils/api';
import { toast } from 'sonner';

interface OperatorRow {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  role: string;
  lastLogin: string | null;
  activities: {
    byStatus: Record<string, number>;
    totalCreatedInPeriod: number;
    completed: number;
  };
  appointments: {
    byStatus: Record<string, number>;
    totalInPeriod: number;
  };
  practicesCreated: number;
  assignedPracticesUpdatedInPeriod: number;
}

type SortKey =
  | 'operator'
  | 'lastLogin'
  | 'practicesCreated'
  | 'assignedPractices'
  | 'activitiesTotal'
  | 'activitiesCompleted'
  | 'appointments';

function formatDateInput(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function operatorLabel(o: OperatorRow): string {
  const n = `${o.firstName || ''} ${o.lastName || ''}`.trim();
  return n || o.email || o.userId;
}

export default function OperatorStatsPage() {
  const [from, setFrom] = useState(() =>
    formatDateInput(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
  );
  const [to, setTo] = useState(() => formatDateInput(new Date()));
  const [userId, setUserId] = useState<string>('all');
  const [operators, setOperators] = useState<OperatorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [baselineLoading, setBaselineLoading] = useState(false);
  const [baselineDateInput, setBaselineDateInput] = useState('');
  const [statsBaselineAt, setStatsBaselineAt] = useState<string | null>(null);
  const [effectiveFromIso, setEffectiveFromIso] = useState<string | null>(null);
  const [userList, setUserList] = useState<Array<{ id: string; firstName?: string; lastName?: string; email?: string }>>([]);

  const [sortKey, setSortKey] = useState<SortKey>('operator');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const { activityTypes, fetchActivityTypes } = useActivityTypeStore();
  const [operatorModalOpen, setOperatorModalOpen] = useState(false);
  const [modalOperator, setModalOperator] = useState<OperatorRow | null>(null);
  const [modalActivities, setModalActivities] = useState<Activity[]>([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalPage, setModalPage] = useState(1);
  const [practiceSortOrder, setPracticeSortOrder] = useState<'asc' | 'desc'>('desc');
  const [expandedPractices, setExpandedPractices] = useState<Set<string>>(new Set());
  const [modalPagination, setModalPagination] = useState<{
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    totalActivities?: number;
  } | null>(null);

  const groupedModalActivities = useMemo<PracticeGroup[]>(() => {
    const map = new Map<string, PracticeGroup>();
    const order: string[] = [];
    for (const a of modalActivities) {
      const pid = a.practiceId;
      if (!map.has(pid)) {
        order.push(pid);
        map.set(pid, {
          practiceId: pid,
          practiceNumber: a.practice?.practiceNumber ?? null,
          title: a.practice?.title || 'Senza titolo',
          clientName: a.practice?.client
            ? `${a.practice.client.firstName} ${a.practice.client.lastName}`
            : '',
          typeName: a.practice?.type?.name || '',
          activities: [],
        });
      }
      map.get(pid)!.activities.push(a);
    }
    return order.map((pid) => map.get(pid)!);
  }, [modalActivities]);

  const loadModalActivities = useCallback(async () => {
    if (!modalOperator) return;
    try {
      setModalLoading(true);
      const params = new URLSearchParams();
      params.set('userId', modalOperator.userId);
      params.set('status', 'OPEN');
      params.set('groupByPractice', 'true');
      params.set('page', String(modalPage));
      params.set('limit', '25');
      params.set('practiceSortBy', 'createdAt');
      params.set('practiceSortOrder', practiceSortOrder);
      const res = await apiClient.get(`/activities?${params.toString()}`);
      setModalActivities(Array.isArray(res.data?.data) ? res.data.data : []);
      setModalPagination(res.data?.pagination ?? null);
    } catch {
      toast.error('Impossibile caricare le attività dell’operatore');
      setModalActivities([]);
      setModalPagination(null);
    } finally {
      setModalLoading(false);
    }
  }, [modalOperator, modalPage, practiceSortOrder]);

  useEffect(() => {
    if (operatorModalOpen && modalOperator) {
      void loadModalActivities();
    }
  }, [operatorModalOpen, modalOperator, loadModalActivities]);

  const openOperatorModal = (o: OperatorRow) => {
    setModalOperator(o);
    setModalPage(1);
    setPracticeSortOrder('desc');
    setExpandedPractices(new Set());
    setModalActivities([]);
    setModalPagination(null);
    setOperatorModalOpen(true);
    void fetchActivityTypes(true);
  };

  const closeOperatorModal = (open: boolean) => {
    if (open) return;
    setOperatorModalOpen(false);
    setModalOperator(null);
    setModalActivities([]);
    setModalPagination(null);
    setModalPage(1);
    setPracticeSortOrder('desc');
    setExpandedPractices(new Set());
  };

  const toggleModalPracticeExpand = (pid: string) => {
    setExpandedPractices((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  useEffect(() => {
    apiClient
      .get('/users')
      .then((r) => {
        const d = r.data?.data;
        setUserList(Array.isArray(d) ? d : []);
      })
      .catch(() => {});
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('from', new Date(from + 'T00:00:00').toISOString());
      params.set('to', new Date(to + 'T23:59:59.999').toISOString());
      if (userId && userId !== 'all') params.set('userId', userId);
      const res = await apiClient.get(`/admin/operator-stats?${params.toString()}`);
      const d = res.data?.data;
      setOperators(d?.operators || []);
      setStatsBaselineAt(d?.statsBaselineAt ?? null);
      setEffectiveFromIso(d?.from ?? null);
    } catch {
      toast.error('Impossibile caricare le statistiche');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setBaselineFromNow = async () => {
    try {
      setBaselineLoading(true);
      await apiClient.post('/admin/operator-stats/baseline', {});
      toast.success('Baseline impostata: i contatori partono da adesso.');
      const today = formatDateInput(new Date());
      setFrom(today);
      await load();
    } catch {
      toast.error('Impossibile salvare la baseline');
    } finally {
      setBaselineLoading(false);
    }
  };

  const setBaselineFromChosenDate = async () => {
    if (!baselineDateInput.trim()) {
      toast.error('Seleziona una data');
      return;
    }
    const d = new Date(baselineDateInput + 'T00:00:00');
    if (Number.isNaN(d.getTime())) {
      toast.error('Data non valida');
      return;
    }
    try {
      setBaselineLoading(true);
      await apiClient.post('/admin/operator-stats/baseline', { baselineAt: d.toISOString() });
      toast.success('Baseline impostata: conteggi da quella data.');
      setFrom(baselineDateInput);
      await load();
    } catch {
      toast.error('Impossibile salvare la baseline');
    } finally {
      setBaselineLoading(false);
    }
  };

  const userOptions = useMemo((): { id: string; label: string }[] => {
    return userList.map((u) => ({
      id: u.id,
      label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || u.id,
    }));
  }, [userList]);

  const toggleSort = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        setSortDir(key === 'operator' ? 'asc' : 'desc');
      }
    },
    [sortKey]
  );

  const sortedOperators = useMemo(() => {
    const arr = [...operators];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      switch (sortKey) {
        case 'operator':
          return operatorLabel(a).localeCompare(operatorLabel(b), 'it') * dir;
        case 'lastLogin': {
          const ta = a.lastLogin ? new Date(a.lastLogin).getTime() : null;
          const tb = b.lastLogin ? new Date(b.lastLogin).getTime() : null;
          if (ta === null && tb === null) return 0;
          if (ta === null) return 1;
          if (tb === null) return -1;
          return (ta - tb) * dir;
        }
        case 'practicesCreated':
          return (a.practicesCreated - b.practicesCreated) * dir;
        case 'assignedPractices':
          return (a.assignedPracticesUpdatedInPeriod - b.assignedPracticesUpdatedInPeriod) * dir;
        case 'activitiesTotal':
          return (a.activities.totalCreatedInPeriod - b.activities.totalCreatedInPeriod) * dir;
        case 'activitiesCompleted':
          return (a.activities.completed - b.activities.completed) * dir;
        case 'appointments':
          return (a.appointments.totalInPeriod - b.appointments.totalInPeriod) * dir;
        default:
          return 0;
      }
    });
    return arr;
  }, [operators, sortKey, sortDir]);

  const SortHead = ({
    k,
    label,
    align = 'left',
  }: {
    k: SortKey;
    label: string;
    align?: 'left' | 'right';
  }) => {
    const active = sortKey === k;
    const Icon = !active ? ArrowUpDown : sortDir === 'asc' ? ArrowUp : ArrowDown;
    return (
      <th className={`p-3 font-medium ${align === 'right' ? 'text-right' : 'text-left'}`}>
        <button
          type="button"
          onClick={() => toggleSort(k)}
          className={`inline-flex items-center gap-1 hover:text-foreground text-muted-foreground hover:bg-muted/80 rounded px-1 -mx-1 ${
            align === 'right' ? 'w-full justify-end' : ''
          }`}
        >
          {label}
          <Icon className="h-3.5 w-3.5 shrink-0" />
        </button>
      </th>
    );
  };

  const exportCsv = () => {
    const headers = [
      'Operatore',
      'Email',
      'Ruolo',
      'Ultimo login',
      'Pratiche create',
      'Pratiche assegnate (agg. periodo)',
      'Attività totali',
      'Attività completate',
      'Appuntamenti totali',
    ];
    const rows = sortedOperators.map((o) => [
      `"${(`${o.firstName || ''} ${o.lastName || ''}`).trim().replace(/"/g, '""')}"`,
      o.email || '',
      o.role,
      o.lastLogin || '',
      String(o.practicesCreated),
      String(o.assignedPracticesUpdatedInPeriod),
      String(o.activities.totalCreatedInPeriod),
      String(o.activities.completed),
      String(o.appointments.totalInPeriod),
    ]);
    const csv = [headers.join(';'), ...rows.map((r) => r.join(';'))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `operator-stats-${from}-${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const effectiveFromLabel =
    statsBaselineAt && effectiveFromIso
      ? new Date(effectiveFromIso).toLocaleDateString('it-IT', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      : null;

  return (
    <div className="w-full min-w-0 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Statistiche operatori</h1>
            <p className="text-sm text-muted-foreground">
              Attività da timeline pratiche, attività e appuntamenti nel periodo selezionato.
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={exportCsv} disabled={!operators.length}>
          <Download className="h-4 w-4 mr-2" />
          Esporta CSV
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Contatori (nuovo avvio)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Per azzerare i numeri rispetto ai test precedenti senza cancellare dati nel database, imposta una{' '}
            <strong>baseline</strong>: dal momento scelto in poi i conteggi rispettano solo eventi successivi (se il filtro
            &quot;Da&quot; è antecedente, viene usata automaticamente la data della baseline).
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={setBaselineFromNow}
              disabled={baselineLoading}
            >
              {baselineLoading ? 'Salvataggio...' : 'Imposta baseline da adesso'}
            </Button>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <Label htmlFor="baseline-date" className="text-xs text-muted-foreground">
                  Oppure da data (es. lunedì)
                </Label>
                <Input
                  id="baseline-date"
                  type="date"
                  value={baselineDateInput}
                  onChange={(e) => setBaselineDateInput(e.target.value)}
                  className="mt-1 w-[160px]"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={setBaselineFromChosenDate}
                disabled={baselineLoading || !baselineDateInput}
              >
                Usa questa data
              </Button>
            </div>
            {statsBaselineAt && (
              <span className="text-sm text-muted-foreground">
                Baseline salvata:{' '}
                {new Date(statsBaselineAt).toLocaleString('it-IT', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filtri</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-4 items-end">
          <div>
            <Label htmlFor="stats-from">Da</Label>
            <Input
              id="stats-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="mt-1 w-[160px]"
            />
          </div>
          <div>
            <Label htmlFor="stats-to">A</Label>
            <Input
              id="stats-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="mt-1 w-[160px]"
            />
          </div>
          <div>
            <Label>Operatore</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger className="mt-1 w-[220px]">
                <SelectValue placeholder="Tutti" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {userOptions.map(({ id, label }) => (
                  <SelectItem key={id} value={id}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={load} disabled={loading}>
            {loading ? 'Caricamento...' : 'Aggiorna'}
          </Button>
        </CardContent>
      </Card>

      {effectiveFromLabel && (
        <p className="text-sm text-muted-foreground px-1">
          Data inizio conteggi (non prima della baseline): <strong>{effectiveFromLabel}</strong>
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Riepilogo</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {loading ? (
            <p className="text-muted-foreground">Caricamento...</p>
          ) : operators.length === 0 ? (
            <p className="text-muted-foreground">Nessun dato nel periodo.</p>
          ) : (
            <div className="rounded-md border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <SortHead k="operator" label="Operatore" />
                    <SortHead k="lastLogin" label="Ultimo login" />
                    <SortHead k="practicesCreated" label="Pratiche create" align="right" />
                    <SortHead k="assignedPractices" label="Pratiche assegnate (agg.)" align="right" />
                    <SortHead k="activitiesTotal" label="Attività" align="right" />
                    <SortHead k="activitiesCompleted" label="Attività completate" align="right" />
                    <SortHead k="appointments" label="Appuntamenti" align="right" />
                  </tr>
                </thead>
                <tbody>
                  {sortedOperators.map((o) => (
                    <tr key={o.userId} className="border-b last:border-0">
                      <td className="p-0">
                        <button
                          type="button"
                          className="w-full text-left p-3 font-medium hover:bg-muted/60 rounded-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          onClick={() => openOperatorModal(o)}
                          aria-expanded={operatorModalOpen && modalOperator?.userId === o.userId}
                        >
                          {`${o.firstName || ''} ${o.lastName || ''}`.trim() || o.email}
                          <span className="block text-xs text-muted-foreground font-normal">{o.email}</span>
                        </button>
                      </td>
                      <td className="p-3">
                        {o.lastLogin ? new Date(o.lastLogin).toLocaleString('it-IT') : '—'}
                      </td>
                      <td className="p-3 text-right">{o.practicesCreated}</td>
                      <td className="p-3 text-right">{o.assignedPracticesUpdatedInPeriod}</td>
                      <td className="p-3 text-right">{o.activities.totalCreatedInPeriod}</td>
                      <td className="p-3 text-right">{o.activities.completed}</td>
                      <td className="p-3 text-right">{o.appointments.totalInPeriod}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={operatorModalOpen} onOpenChange={closeOperatorModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Attività aperte — {modalOperator ? operatorLabel(modalOperator) : ''}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              Pratiche con attività assegnate (PENDING / IN_PROGRESS), ordinate per data creazione pratica.
            </p>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-2 pb-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => {
                setPracticeSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
                setModalPage(1);
              }}
            >
              <ArrowDownUp className="h-4 w-4" />
              Ordine pratiche: {practiceSortOrder === 'desc' ? 'più recenti prima' : 'più vecchie prima'}
            </Button>
          </div>
          {modalLoading && !modalActivities.length ? (
            <p className="text-sm text-muted-foreground py-6">Caricamento…</p>
          ) : !groupedModalActivities.length ? (
            <p className="text-sm text-muted-foreground py-6">Nessuna attività aperta assegnata a questo operatore.</p>
          ) : (
            <PracticeActivityGroups
              groups={groupedModalActivities}
              expandedPractices={expandedPractices}
              onTogglePractice={toggleModalPracticeExpand}
              renderActivity={(activity) => (
                <ActivityRowReadOnly activity={activity} activityTypes={activityTypes} />
              )}
            />
          )}
          {modalPagination && modalPagination.totalPages > 1 && (
            <div className="flex items-center justify-between gap-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Pagina {modalPagination.page} di {modalPagination.totalPages}
                {modalPagination.totalActivities != null
                  ? ` · ${modalPagination.totalActivities} attività totali`
                  : ''}
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={modalPagination.page <= 1 || modalLoading}
                  onClick={() => setModalPage((p) => Math.max(1, p - 1))}
                >
                  Precedente
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={modalPagination.page >= modalPagination.totalPages || modalLoading}
                  onClick={() => setModalPage((p) => p + 1)}
                >
                  Successiva
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
